import { formatNumber } from "../../utils/FormattingUtils";
import { Action, ActionBase, DoActionArgs } from "../actions/Action";
import { registerAction } from "../actions/Actions";
import { getSmarterApprenticesTurnedOn } from "../autocast/Autocast";
import { registerClearCacheListener } from "../Caching";
import { applyTransformations } from "../calculation/Calculation";
import { TransformationTags } from "../calculation/TransformationTags";
import { PlayerContextState } from "../PlayerContext";
import { grantResource, Resource } from "../Resources";
import { hasTemporaryEffect } from "../timetick/TemporaryEffects";
import { getSecondsPlayed } from "../timetick/TotalTimePlayed";
import {
  grantSchoolExpByUsage,
  meetsRequirements,
  School,
  SpellElement,
} from "./ElementsAndSchools";
import {
  addRecentSpellCast,
  getAllSpells,
  registerSpell,
  setLastCastedTime,
} from "./Spells";
import { isCastingRecommendedTemporaryEffect } from "./Utils";

export interface Spell extends Action {
  getSpellName(): string;
  canAfford(state: PlayerContextState): boolean;
  getSchoolLevelRequirements(): Partial<Record<School, number>>;
  getElement(): SpellElement | undefined;
  isCastingRecommended(state: PlayerContextState): boolean;
  getEstimatedMinAutocastDelay(state: PlayerContextState): number;
}

export abstract class SpellActionBase extends ActionBase implements Spell {
  abstract getSpellName(): string;
  abstract getManaCost(state: PlayerContextState): number;
  abstract doSpellAction(
    state: PlayerContextState,
    args: DoActionArgs,
  ): PlayerContextState;
  abstract getSchoolLevelRequirements(): Partial<Record<School, number>>;

  constructor() {
    super();
    registerSpell(this);
    registerAction(this);
  }

  getElement(): SpellElement | undefined {
    return undefined;
  }

  _tagArray: string[] | null = null;

  getTags(): string[] {
    if (this._tagArray == null) {
      const tags = [
        ...super.getTags(),
        TransformationTags.Spell,
        ...Object.keys(this.getSchoolLevelRequirements()),
      ];
      const element = this.getElement();
      if (element) {
        tags.push(element);
      }
      this._tagArray = tags;
    }
    return this._tagArray;
  }

  getName(): string {
    return this.getSpellName();
  }

  _manaCostTags: string[] | null = null;
  getCost(state: PlayerContextState): {
    resources: Partial<Record<Resource, number>>;
    items: Record<string, number>;
  } {
    if (this._manaCostTags == null) {
      this._manaCostTags = [
        ...this.getTags(),
        TransformationTags.Cost,
        Resource.Mana,
      ];
    }
    return {
      resources: {
        Mana: applyTransformations(
          this._manaCostTags,
          state,
          this.getManaCost(state),
        ),
      },
      items: {},
    };
  }

  getDisplayName(state: PlayerContextState): string {
    const lastCastTime = state?.lastSpellCastTimes?.[this.getSpellName()] || 0;
    if (lastCastTime > 0) {
      const cooldown = lastCastTime + this.getCooldown(state);
      const secTillOver = cooldown - getSecondsPlayed(state);
      if (secTillOver > 0) {
        return `${this.getName()} (${formatNumber(secTillOver, {
          showDecimals: true,
        })} sec)`;
      }
    }
    return super.getDisplayName(state);
  }

  getCategory(): string {
    return "Spells";
  }

  getBaseCooldown(): number {
    return 0.0;
  }

  _cooldownTagArray: string[] | null = null;
  getCooldown(state: PlayerContextState): number {
    if (this._cooldownTagArray == null) {
      this._cooldownTagArray = [
        ...this.getTags(),
        TransformationTags.SpellCooldown,
      ];
    }
    if (this.getBaseCooldown() === 0.0) {
      return 0.0;
    }
    return applyTransformations(
      this._cooldownTagArray,
      state,
      this.getBaseCooldown(),
    );
  }

  // If a spell is visible after 10 seconds of play,
  // assume it will be visible forever until cache is invalidated
  wasVisibleSecondsPlayed: number | undefined = undefined;
  invalidateVisibilityCache(): void {
    this.wasVisibleSecondsPlayed = undefined;
  }

  isVisible(state: PlayerContextState): boolean {
    const secondsPlayed = getSecondsPlayed(state);
    if (this.wasVisibleSecondsPlayed != null && !state.isFake) {
      if (secondsPlayed > this.wasVisibleSecondsPlayed) {
        return true;
      } else {
        this.wasVisibleSecondsPlayed = undefined;
      }
    }
    const schoolRequirements = this.getSchoolLevelRequirements();
    const isVisible = meetsRequirements(state, schoolRequirements);
    if (isVisible && !state.isFake) {
      this.wasVisibleSecondsPlayed = secondsPlayed;
    }
    return isVisible;
  }

  isEnabled(state: PlayerContextState): boolean {
    if (!this.isVisible(state)) {
      return false;
    }

    const lastCastTime = state?.lastSpellCastTimes?.[this.getSpellName()] || 0;
    if (
      lastCastTime > 0 &&
      getSecondsPlayed(state) < lastCastTime + this.getCooldown(state)
    ) {
      return false;
    }

    if (
      hasTemporaryEffect(state, "SilencePlayer") ||
      hasTemporaryEffect(state, "PlayerStun")
    ) {
      return false;
    }

    const transformationResult = applyTransformations(
      [...this.getTags(), TransformationTags.CanCastSpell],
      state,
      1.0,
    );
    if (transformationResult <= 0) {
      return false;
    }

    return super.isEnabled(state);
  }

  doAction(args: DoActionArgs, state: PlayerContextState): PlayerContextState {
    if (!this.canAfford(state)) {
      return state;
    }

    const cost = this.getCost(state);
    (Object.keys(cost.resources) as Array<Resource>).forEach((resourceName) => {
      state = grantResource(
        resourceName,
        -(cost?.resources?.[resourceName] ?? 0),
      )(state);
    });

    const schoolRequirements = this.getSchoolLevelRequirements();
    Object.keys(schoolRequirements).forEach((school) => {
      state = grantSchoolExpByUsage(
        state,
        school as School,
        schoolRequirements[school as School] ?? 0,
        this.getTags(),
      );
    });

    if (!args.isAutomatic) {
      state = addRecentSpellCast(this)(state);
    }

    state = setLastCastedTime(this)(state);

    return this.doSpellAction(state, args);
  }

  isCastingRecommended(state: PlayerContextState): boolean {
    if (!isCastingRecommendedTemporaryEffect(state, this.getId())) {
      return false;
    }
    return true;
  }

  getEstimatedMinAutocastDelay(state: PlayerContextState): number {
    if (!getSmarterApprenticesTurnedOn(state)) {
      return Math.max(0, this.getCooldown(state));
    }
    let duration = 0;
    const effects = this.getActionEffects(state, "duration");
    if (effects?.duration) {
      duration = effects.duration;
    }
    return Math.max(0, Math.floor(duration - 2), this.getCooldown(state));
  }
}

registerClearCacheListener("SpellCacheClearer", () => {
  Object.values(getAllSpells()).forEach(
    (spell) =>
      spell instanceof SpellActionBase && spell.invalidateVisibilityCache(),
  );
});
